import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { usePermission } from '../context/PermissionContext';
import StatCards from '../components/StatCards';
import UpdateSuccessModal from '../components/UpdateSuccessModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import DeleteSuccessModal from '../components/DeleteSuccessModal';
import {
  createProduct, getAllProducts, getAllProductsUnpaginated, updateProduct, deleteProduct,
  getAllCategories, getAllSubcategories, toggleProductStatus, getProductById, IMAGE_BASE_URL
} from '../services/api';
import './Products.css';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import DOMPurify from 'dompurify';

var ITEMS_PER_PAGE = 10;
var NAME_LIMIT = 80;
var DESC_LIMIT = 2000;
var MAX_IMAGE_BYTES = 3.4 * 1024 * 1024; // 3.4 MB

var quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean']
  ]
};

var PRODUCT_IMAGE_BASE_URL = String(IMAGE_BASE_URL || 'https://api.ishmaijewellery.com').replace(/\/+$/, '');

var PLACEHOLDER_PRODUCT_IMG = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="%2394a3b8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>';

var sanitizeRawImagePath = function (path) {
  if (!path) return '';
  if (typeof path === 'object') {
    path = path.url || path.src || path.path || path.image || path.filename || path.secure_url || path.location || '';
  }
  if (typeof path !== 'string') return '';
  var p = path.trim();
  if (p.startsWith('blob:') || p.startsWith('data:')) return p;
  p = p.replace(/\\/g, '/');
  var match = p.match(/^(?:https?:\/\/[^\/]+)?\/?(.*)$/i);
  if (match && match[1]) {
    var sub = match[1].replace(/^\/+/, '');
    if (sub.startsWith('api/uploads/')) sub = sub.replace(/^api\//, '');
    return sub;
  }
  return p;
};

var getCanonicalKey = function (val) {
  if (!val) return '';
  if (typeof val === 'object') {
    if (val.file && typeof val.file === 'object') {
      var fn = (val.file.name || '').trim().toLowerCase();
      var cleanFn = fn.replace(/^\d+[-_]/, '');
      return (cleanFn || fn) + '_' + (val.file.size || 0);
    }
    if (val.name && val.size) {
      var nName = String(val.name).trim().toLowerCase();
      var cleanNName = nName.replace(/^\d+[-_]/, '');
      return (cleanNName || nName) + '_' + val.size;
    }
    val = val.preview || val.url || val.src || val.path || val.image || val.filename || val.secure_url || val.location || '';
  }
  if (typeof val !== 'string') return '';
  var s = val.trim();
  if (!s) return '';
  if (s.startsWith('blob:')) return s.toLowerCase();
  if (s.startsWith('data:')) return s.slice(0, 100).toLowerCase();

  s = s.replace(/\\/g, '/');
  s = s.replace(/^https?:\/\/[^\/]+/i, '');
  s = s.replace(/^\/+/, '');
  if (s.startsWith('api/')) s = s.replace(/^api\//, '');
  if (s.startsWith('uploads/')) s = s.replace(/^uploads\//, '');
  s = s.split('?')[0].split('#')[0];
  var filename = s.toLowerCase().split('/').pop() || s.toLowerCase();
  var cleanFilename = filename.replace(/^\d+[-_]/, '');
  return cleanFilename || filename;
};

var getBaseCanonicalKey = function (val) {
  var key = getCanonicalKey(val);
  if (!key) return '';
  return key.split('_')[0];
};

var resolveImageUrl = function (url) {
  if (!url) return '';
  if (typeof url === 'string' && (url.trim().startsWith('[') || url.trim().startsWith('{'))) {
    try {
      var parsed = JSON.parse(url);
      if (Array.isArray(parsed) && parsed.length > 0) return resolveImageUrl(parsed[0]);
      if (parsed && typeof parsed === 'object') return resolveImageUrl(parsed);
    } catch (e) { }
  }
  if (typeof url === 'object') {
    url = url.url || url.src || url.path || url.image || url.filename || url.secure_url || url.location || '';
  }
  if (typeof url !== 'string' || !url.trim()) return '';
  var u = url.trim();
  if (u.startsWith('blob:') || u.startsWith('data:')) return u;

  u = u.replace(/\\/g, '/');
  var base = String(PRODUCT_IMAGE_BASE_URL || 'https://api.ishmaijewellery.com').replace(/\/+$/, '');

  if (u.startsWith('http://') || u.startsWith('https://')) {
    var idx = u.indexOf('/uploads/');
    if (idx !== -1) {
      return base + u.substring(idx);
    }
    return u;
  }

  var cleanPath = u.replace(/^\/+/, '');
  if (cleanPath.startsWith('api/uploads/')) {
    cleanPath = cleanPath.replace(/^api\//, '');
  }

  return base + '/' + cleanPath;
};

var handleImgError = function (e, rawSrc) {
  var target = e.target;
  if (!target) return;

  if (target.dataset.triedFallback) {
    target.src = PLACEHOLDER_PRODUCT_IMG;
    target.style.opacity = '0.6';
    return;
  }
  target.dataset.triedFallback = 'true';

  var base = String(PRODUCT_IMAGE_BASE_URL || 'https://api.ishmaijewellery.com').replace(/\/+$/, '');
  var currentSrc = target.src || '';

  if (rawSrc) {
    var clean = sanitizeRawImagePath(rawSrc);
    if (clean && currentSrc.indexOf(clean) === -1) {
      target.src = base + '/' + clean.replace(/^\/+/, '');
      return;
    }
  }

  if (currentSrc && currentSrc.indexOf('/uploads/') === -1 && currentSrc.indexOf('data:') === -1) {
    var parts = currentSrc.split('/');
    var filename = parts[parts.length - 1];
    if (filename) {
      target.src = base + '/uploads/' + filename;
      return;
    }
  }

  target.src = PLACEHOLDER_PRODUCT_IMG;
  target.style.opacity = '0.6';
};

var extractImageSrc = function (obj) {
  var list = extractImageList(obj, true);
  return list.length ? list[0] : '';
};

var extractImageRaw = function (obj) {
  var list = extractImageList(obj, false);
  return list.length ? list[0] : '';
};

var extractImageList = function (obj, resolved) {
  if (!obj || typeof obj !== 'object') return [];
  var out = [];
  var seenKeys = {};

  var addValue = function (value) {
    if (!value) return;
    if (typeof value === 'string' && (value.trim().startsWith('[') || value.trim().startsWith('{'))) {
      try {
        var parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          parsed.forEach(addValue);
          return;
        } else if (parsed && typeof parsed === 'object') {
          value = parsed;
        }
      } catch (e) { }
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
      value = value.url || value.src || value.path || value.image || value.filename || value.secure_url || value.location || '';
    }
    if (typeof value === 'string' && value.trim()) {
      var raw = value.trim();
      if (raw.startsWith('blob:') || raw.startsWith('data:')) return;
      var clean = sanitizeRawImagePath(raw);
      var targetUrl = resolved === false ? clean : resolveImageUrl(clean);
      var baseKey = getBaseCanonicalKey(clean || raw || targetUrl);
      if (targetUrl && baseKey && !seenKeys[baseKey]) {
        seenKeys[baseKey] = true;
        out.push(targetUrl);
      }
    }
  };

  if (Array.isArray(obj.images)) {
    obj.images.forEach(addValue);
  } else if (typeof obj.images === 'string') {
    addValue(obj.images);
  }

  if (obj.image) addValue(obj.image);
  if (obj.productImage) addValue(obj.productImage);
  if (obj.thumbnail) addValue(obj.thumbnail);

  return out;
};

// ── Icons ──────────────────────────────────────────────────────────────────────
var EyeIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('path', { d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' }),
    React.createElement('circle', { cx: 12, cy: 12, r: 3 })
  );
};
var PencilIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('path', { d: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7' }),
    React.createElement('path', { d: 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z' })
  );
};
var TrashIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('polyline', { points: '3 6 5 6 21 6' }),
    React.createElement('path', { d: 'M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6' }),
    React.createElement('path', { d: 'M10 11v6M14 11v6' }),
    React.createElement('path', { d: 'M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2' })
  );
};
var UploadIcon = function () {
  return React.createElement('svg', { width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('rect', { x: 3, y: 3, width: 18, height: 18, rx: 3 }),
    React.createElement('circle', { cx: 8.5, cy: 8.5, r: 1.5 }),
    React.createElement('polyline', { points: '21 15 16 10 5 21' })
  );
};
var XIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' },
    React.createElement('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
    React.createElement('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
  );
};
var ChevronIcon = function (props) {
  var open = props.open;
  return React.createElement('svg', {
    width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5,
    strokeLinecap: 'round', strokeLinejoin: 'round',
    style: { transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }
  },
    React.createElement('polyline', { points: '6 9 12 15 18 9' })
  );
};
var WarnIcon = function () {
  return React.createElement('svg', { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: '#f59e0b', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', style: { flexShrink: 0 } },
    React.createElement('path', { d: 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' }),
    React.createElement('line', { x1: 12, y1: 9, x2: 12, y2: 13 }),
    React.createElement('line', { x1: 12, y1: 17, x2: 12.01, y2: 17 })
  );
};

var NoImgPlaceholder = function () {
  return React.createElement('div', {
    style: {
      width: '100%', height: '100%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(62,124,140,0.08)', borderRadius: 6,
    }
  },
    React.createElement('svg', { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: '#475569', strokeWidth: 1.5 },
      React.createElement('rect', { x: 3, y: 3, width: 18, height: 18, rx: 3 }),
      React.createElement('circle', { cx: 8.5, cy: 8.5, r: 1.5 }),
      React.createElement('polyline', { points: '21 15 16 10 5 21' })
    )
  );
};

// ── Custom Select (matches theme, no native browser highlight) ─────────────
var CustomSelect = function (props) {
  var value = props.value; var onChange = props.onChange; var options = props.options;
  var disabled = props.disabled;
  var os = useState(false); var open = os[0]; var setOpen = os[1];
  var ref = useRef();

  useEffect(function () {
    var handler = function (e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return function () { document.removeEventListener('mousedown', handler); };
  }, []);

  var selected = options.find(function (o) { return o.value === value; });

  return React.createElement('div', { ref: ref, style: { position: 'relative' } },
    React.createElement('button', {
      type: 'button',
      disabled: disabled,
      onClick: function () { if (!disabled) setOpen(!open); },
      style: {
        width: '100%', textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer',
        padding: '11px 14px', borderRadius: 10,
        border: open ? '1px solid #3E7C8C' : '1px solid rgba(62,124,140,0.28)',
        background: disabled ? 'rgba(241,245,249,0.86)' : '#FFFFFF',
        color: disabled ? '#94a3b8' : '#102a33',
        fontFamily: "'Outfit',sans-serif", fontSize: 13,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        opacity: disabled ? 0.6 : 1,
      }
    },
      selected ? selected.label : 'Select',
      React.createElement('span', { style: { fontSize: 11, color: '#2d5f6e', marginLeft: 8 } }, '\u25BC')
    ),
    open && !disabled && React.createElement('div', {
      className: 'cs-dropdown-list',
      style: {
        position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
        background: '#ffffff', border: '1px solid rgba(62,124,140,0.22)',
        borderRadius: 10, zIndex: 50,
        boxShadow: '0 16px 40px rgba(15,23,42,0.16)',
      }
    },
      options.map(function (o) {
        var isSel = o.value === value;
        return React.createElement('div', {
          key: o.value,
          onClick: function () { onChange(o.value); setOpen(false); },
          style: {
            padding: '10px 14px', fontSize: 13, cursor: 'pointer',
            color: isSel ? '#ffffff' : '#102a33',
            background: isSel ? '#3E7C8C' : '#ffffff',
            fontFamily: "'Outfit',sans-serif",
            transition: 'background 0.15s',
          },
          onMouseEnter: function (e) { if (!isSel) e.currentTarget.style.background = '#eef8fa'; },
          onMouseLeave: function (e) { if (!isSel) e.currentTarget.style.background = '#ffffff'; }
        }, o.label);
      })
    )
  );
};

var ProductImage = function (props) {
  var product = props.product;
  var alt = props.alt;
  var es = useState(false); var err = es[0]; var setErr = es[1];
  var src = extractImageSrc(product);
  useEffect(function () { setErr(false); }, [src]); // eslint-disable-line
  if (!src || err) return React.createElement(NoImgPlaceholder, null);
  return React.createElement('img', {
    src: src,
    alt: alt || 'product',
    style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: 6 },
    onError: function () { setErr(true); }
  });
};

// ── Image Size Warning Modal ───────────────────────────────────────────────────
var ImageSizeWarningModal = function (props) {
  var onClose = props.onClose;
  return React.createElement('div', {
    onClick: function (e) { if (e.target === e.currentTarget) onClose(); },
    style: {
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.80)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 99999,
      padding: 20,
    }
  },
    React.createElement('div', {
      style: {
        width: '100%', maxWidth: 440,
        borderRadius: 20,
        background: '#ffffff',
        border: '2px solid #3E7C8C',
        boxShadow: '0 32px 80px rgba(62,124,140,0.16)',
        padding: '44px 36px 36px',
        textAlign: 'center',
      }
    },
      React.createElement('div', {
        style: {
          width: 76, height: 76, borderRadius: '50%',
          margin: '0 auto 24px',
          background: '#eff6ff',
          border: '3px solid #3E7C8C',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }
      },
        React.createElement('svg', {
          width: 38, height: 38, viewBox: '0 0 24 24',
          fill: 'none', stroke: '#3E7C8C', strokeWidth: 2.2,
          strokeLinecap: 'round', strokeLinejoin: 'round'
        },
          React.createElement('path', { d: 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' }),
          React.createElement('line', { x1: 12, y1: 9, x2: 12, y2: 13 }),
          React.createElement('line', { x1: 12, y1: 17, x2: 12.01, y2: 17 })
        )
      ),
      React.createElement('h3', {
        style: { color: '#3E7C8C', fontSize: 22, fontWeight: 800, margin: '0 0 14px', fontFamily: "'Outfit',sans-serif" }
      }, 'Image Size Too Large!'),
      React.createElement('p', {
        style: { color: '#111827', fontSize: 16, margin: '0 0 10px', lineHeight: 1.6, fontFamily: "'Outfit',sans-serif", fontWeight: 600 }
      },
        'Please upload an image of size\u00A0',
        React.createElement('span', { style: { color: '#F28C28' } }, '3.4\u00A0MB or below'), '.'
      ),
      React.createElement('p', {
        style: { color: '#475569', fontSize: 13, margin: '0 0 32px', fontFamily: "'Outfit',sans-serif" }
      }, 'Your file exceeds the 3.4\u00A0MB limit. Compress it using TinyPNG or Squoosh and try again.'),
      React.createElement('button', {
        onClick: onClose,
        style: {
          padding: '13px 52px', borderRadius: 12,
          border: 'none', background: 'linear-gradient(90deg, #3E7C8C, #F28C28)',
          color: '#fff', fontWeight: 700, fontSize: 15,
          cursor: 'pointer', fontFamily: "'Outfit',sans-serif",
          boxShadow: '0 8px 24px rgba(62,124,140,0.24)',
        }
      }, 'OK, Got it')
    )
  );
};

// ── Image Type Warning Modal ───────────────────────────────────────────────────
var ImageTypeWarningModal = function (props) {
  var onClose = props.onClose;
  return React.createElement('div', {
    onClick: function (e) { if (e.target === e.currentTarget) onClose(); },
    style: {
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.80)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 99999,
      padding: 20,
    }
  },
    React.createElement('div', {
      style: {
        width: '100%', maxWidth: 440,
        borderRadius: 20,
        background: '#ffffff',
        border: '2px solid #3E7C8C',
        boxShadow: '0 32px 80px rgba(62,124,140,0.16)',
        padding: '44px 36px 36px',
        textAlign: 'center',
      }
    },
      React.createElement('div', {
        style: {
          width: 76, height: 76, borderRadius: '50%',
          margin: '0 auto 24px',
          background: '#eff6ff',
          border: '3px solid #3E7C8C',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }
      },
        React.createElement('svg', {
          width: 38, height: 38, viewBox: '0 0 24 24',
          fill: 'none', stroke: '#3E7C8C', strokeWidth: 2.2,
          strokeLinecap: 'round', strokeLinejoin: 'round'
        },
          React.createElement('path', { d: 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' }),
          React.createElement('line', { x1: 12, y1: 9, x2: 12, y2: 13 }),
          React.createElement('line', { x1: 12, y1: 17, x2: 12.01, y2: 17 })
        )
      ),
      React.createElement('h3', {
        style: { color: '#3E7C8C', fontSize: 22, fontWeight: 800, margin: '0 0 14px', fontFamily: "'Outfit',sans-serif" }
      }, 'Invalid File Type!'),
      React.createElement('p', {
        style: { color: '#111827', fontSize: 16, margin: '0 0 10px', lineHeight: 1.6, fontFamily: "'Outfit',sans-serif", fontWeight: 600 }
      },
        'Please upload an image in ',
        React.createElement('span', { style: { color: '#F28C28' } }, 'JPG, PNG, or WEBP format'), '.'
      ),
      React.createElement('p', {
        style: { color: '#475569', fontSize: 13, margin: '0 0 32px', fontFamily: "'Outfit',sans-serif" }
      }, 'Only JPG, PNG, and WEBP files are supported. Convert your image and try again.'),
      React.createElement('button', {
        onClick: onClose,
        style: {
          padding: '13px 52px', borderRadius: 12,
          border: 'none', background: 'linear-gradient(90deg, #3E7C8C, #F28C28)',
          color: '#fff', fontWeight: 700, fontSize: 15,
          cursor: 'pointer', fontFamily: "'Outfit',sans-serif",
          boxShadow: '0 8px 24px rgba(62,124,140,0.24)',
        }
      }, 'OK, Got it')
    )
  );
};

var RefreshIcon = function () {
  return React.createElement('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round', style: { flexShrink: 0 } },
    React.createElement('path', { d: 'M23 4v6h-6' }),
    React.createElement('path', { d: 'M1 20v-6h6' }),
    React.createElement('path', { d: 'M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15' })
  );
};

var ChooseImageModal = function (props) {
  var allImages = props.allImages;
  var selectedIndex = props.selectedIndex;
  var onSelectIndex = props.onSelectIndex;
  var onConfirm = props.onConfirm;
  var onClose = props.onClose;

  return React.createElement('div', {
    onClick: function (e) { if (e.target === e.currentTarget) onClose(); },
    style: {
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.75)',
      backdropFilter: 'blur(6px)',
      WebkitBackdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2147483647, padding: 20,
    }
  },
    React.createElement('div', {
      style: {
        width: '100%', maxWidth: 540, maxHeight: '85vh', overflowY: 'auto',
        borderRadius: 20, background: '#ffffff', border: '1px solid rgba(62,124,140,0.22)',
        padding: 24, boxShadow: '0 32px 80px rgba(0,0,0,0.4)',
        display: 'flex', flexDirection: 'column', gap: 16
      }
    },
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
        React.createElement('h3', { style: { margin: 0, color: '#102a33', fontFamily: "'Outfit',sans-serif", fontSize: 20, fontWeight: 800 } }, 'Choose Image'),
        React.createElement('button', {
          onClick: onClose, type: 'button',
          style: { background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 4 }
        }, React.createElement(XIcon, null))
      ),

      React.createElement('div', {
        style: {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
          gap: 14,
          maxHeight: '48vh',
          overflowY: 'auto',
          padding: '4px 2px'
        }
      },
        allImages.map(function (item, idx) {
          var isSelected = idx === selectedIndex;
          return React.createElement('div', {
            key: item.id || idx,
            onClick: function () { onSelectIndex(idx); },
            style: {
              position: 'relative',
              borderRadius: 12,
              border: isSelected ? '2px solid #2563eb' : '1px solid #cbd5e1',
              boxShadow: isSelected ? '0 0 0 3px rgba(37, 99, 235, 0.25)' : '0 2px 6px rgba(0,0,0,0.05)',
              background: '#f8fafc',
              overflow: 'hidden',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.15s ease'
            }
          },
            React.createElement('div', { style: { position: 'relative', width: '100%', height: 96 } },
              React.createElement('img', {
                src: item.src,
                alt: 'Product tile ' + (idx + 1),
                style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
                onError: function (e) { handleImgError(e, item.rawPath); }
              }),
              isSelected && React.createElement('div', {
                style: {
                  position: 'absolute', top: 6, right: 6,
                  width: 22, height: 22, borderRadius: '50%',
                  background: '#2563eb', color: '#ffffff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 800, boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                }
              }, '\u2713')
            ),
            React.createElement('button', {
              type: 'button',
              onClick: function (e) { e.stopPropagation(); onSelectIndex(idx); },
              style: {
                width: '100%', padding: '6px 0', border: 'none',
                background: isSelected ? '#2563eb' : '#f1f5f9',
                color: isSelected ? '#ffffff' : '#334155',
                fontFamily: "'Outfit',sans-serif", fontSize: 12, fontWeight: 700,
                cursor: 'pointer', transition: 'all 0.15s ease'
              }
            }, isSelected ? 'Select' : 'Select')
          );
        })
      ),

      React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 } },
        React.createElement('button', {
          type: 'button', onClick: onClose,
          style: {
            padding: '9px 20px', borderRadius: 10,
            border: '1px solid #cbd5e1', background: '#f8fafc',
            color: '#334155', fontWeight: 700, fontSize: 13,
            cursor: 'pointer', fontFamily: "'Outfit',sans-serif"
          }
        }, 'Cancel'),
        React.createElement('button', {
          type: 'button', onClick: onConfirm,
          style: {
            padding: '9px 22px', borderRadius: 10,
            border: 'none', background: '#061b3a',
            color: '#ffffff', fontWeight: 700, fontSize: 13,
            cursor: 'pointer', fontFamily: "'Outfit',sans-serif",
            boxShadow: '0 4px 12px rgba(6,27,58,0.2)'
          }
        }, 'Set as Main Image')
      )
    )
  );
};
var resolveName = function (field, list) {
  if (field && typeof field === 'object' && field.name) return field.name;
  if (typeof field === 'string' && field && Array.isArray(list)) {
    var f = list.find(function (i) { return i._id === field || i.id === field; });
    if (f && f.name) return f.name;
  }
  return '\u2014';
};

var stripHtml = function (html) {
  if (!html) return '';
  var tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

var resolveId = function (field) {
  if (!field) return '';
  if (typeof field === 'object' && (field._id || field.id)) return field._id || field.id;
  if (typeof field === 'string') return field;
  return '';
};

var getStock = function (p) {
  var vStock = variantsTotalStock(p);
  if (vStock !== null) return vStock;
  if (p.stock !== undefined && p.stock !== null) return p.stock;
  if (p.stock_quantity !== undefined && p.stock_quantity !== null) return p.stock_quantity;
  return null;
};

var isActiveStatus = function (item) {
  return String((item && item.status) || 'active').toLowerCase() === 'active';
};

var extractArray = function (res) {
  try {
    var d = res && res.data;
    if (!d) return [];
    var arr = [];
    if (Array.isArray(d)) { arr = d; }
    else if (d.data && Array.isArray(d.data)) { arr = d.data; }
    else if (d.result && Array.isArray(d.result)) { arr = d.result; }
    else if (d.products && Array.isArray(d.products)) { arr = d.products; }
    else if (d.categories && Array.isArray(d.categories)) { arr = d.categories; }
    else if (d.subcategories && Array.isArray(d.subcategories)) { arr = d.subcategories; }
    else if (d.data && d.data.products && Array.isArray(d.data.products)) { arr = d.data.products; }
    else if (d.data && d.data.data && Array.isArray(d.data.data)) { arr = d.data.data; }
    else if (d.data && d.data.result && Array.isArray(d.data.result)) { arr = d.data.result; }
    else if (d._id || (d.data && d.data._id)) {
      var item = d._id ? d : d.data;
      arr = [item];
    }
    return arr.filter(function (item) { return item && item.isDeleted !== true && !item.deletedAt; });
  } catch (e) { console.error('extractArray:', e); return []; }
};

var dateInputValue = function (value) {
  if (!value) return '';
  var date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
};

var validateProductName = function (val) {
  if (!val || !val.trim()) {
    return 'Product name is required';
  }
  var trimmed = val.trim();
  if (!/^[A-Za-z\s]+$/.test(trimmed)) {
    return 'Product name must contain only letters and spaces';
  }
  return '';
};

// ── Pagination ─────────────────────────────────────────────────────────────────
var Pagination = function (props) {
  var cp = props.currentPage; var tp = props.totalPages;
  var opc = props.onPageChange; var ti = props.totalItems;
  if (tp <= 1 && ti <= ITEMS_PER_PAGE) return null;
  var si = ti === 0 ? 0 : (cp - 1) * ITEMS_PER_PAGE + 1;
  var ei = Math.min(cp * ITEMS_PER_PAGE, ti);
  var nums = (function () {
    var pages = []; var mv = 5;
    var s = Math.max(1, cp - Math.floor(mv / 2));
    var e = Math.min(tp, s + mv - 1);
    if (e - s + 1 < mv) s = Math.max(1, e - mv + 1);
    for (var i = s; i <= e; i++) pages.push(i);
    return pages;
  })();

  return React.createElement('div', { className: 'products-pagination' },
    React.createElement('span', { className: 'pagination-info' },
      ti === 0 ? 'No matching products' : ('Showing ' + si + ' \u2013 ' + ei + ' of ' + ti + ' items')
    ),
    React.createElement('div', { className: 'products-page-buttons' },
      React.createElement('button', {
        key: 'first',
        type: 'button',
        disabled: cp === 1,
        onClick: function () { opc(1); },
        title: 'First Page'
      }, '\u00AB'),
      React.createElement('button', {
        key: 'prev',
        type: 'button',
        disabled: cp === 1,
        onClick: function () { opc(Math.max(1, cp - 1)); },
        title: 'Previous Page'
      }, 'Previous'),
      nums.map(function (n) {
        return React.createElement('button', {
          key: n,
          type: 'button',
          className: n === cp ? 'active' : '',
          onClick: function () { opc(n); }
        }, n);
      }),
      React.createElement('button', {
        key: 'next',
        type: 'button',
        disabled: cp === tp || tp === 0,
        onClick: function () { opc(Math.min(tp, cp + 1)); },
        title: 'Next Page'
      }, 'Next'),
      React.createElement('button', {
        key: 'last',
        type: 'button',
        disabled: cp === tp || tp === 0,
        onClick: function () { opc(tp); },
        title: 'Last Page'
      }, '\u00BB')
    )
  );
};

// ── Variants (Size / Price / Weight / Quantity) ─────────────────────────────
var variantRowId = 0;
var makeEmptyVariant = function () {
  variantRowId += 1;
  return { id: 'v' + variantRowId, size: '', price: '', weight: '', stock: '' };
};

var variantsMinPrice = function (p) {
  var list = (p && p.variants) || [];
  if (!list.length) return null;
  var nums = list.map(function (v) { return Number(v.price) || 0; });
  return Math.min.apply(null, nums);
};
var variantsTotalStock = function (p) {
  var list = (p && p.variants) || [];
  if (!list.length) return null;
  return list.reduce(function (sum, v) { return sum + (Number(v.stock) || 0); }, 0);
};

// Images now go up as a real file (multipart/form-data → multer on the
// backend saves it to /uploads and stores just the path), instead of
// base64-encoding the whole image into the JSON body — that base64
// approach is what was bloating every product document/list response.

var isRowValid = function (v) {
  return !!(v.size && String(v.size).trim()) &&
    v.price !== '' && !isNaN(Number(v.price)) &&
    v.weight !== '' && !isNaN(Number(v.weight)) &&
    v.stock !== '' && !isNaN(Number(v.stock));
};

// ── Main Component ─────────────────────────────────────────────────────────────
// ── Variants Editor: "+ Add Variant" opens ONE inline form at a time for the
// row currently being added/edited. The full list of variants is NOT shown
// here anymore — it lives in the separate "View Variants" popup
// (ViewVariantsModal below), reached via the "View Variants (N)" button.
// This keeps the Add/Edit Product modal short regardless of how many
// variants a product has.
var VariantsEditor = function (props) {
  var rows = props.rows;
  var onAdd = props.onAdd;
  var onRemove = props.onRemove;
  var onChange = props.onChange;
  var error = props.error;
  var expandedId = props.expandedId;
  var onToggleExpand = props.onToggleExpand;
  var onViewVariants = props.onViewVariants;

  var fieldLabel = { fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4, display: 'block' };
  var cardStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
    gap: 10,
    alignItems: 'end',
    padding: 12,
    marginBottom: 10,
    border: '1px solid var(--border-color)',
    borderRadius: 10,
    background: 'rgba(123, 44, 255, 0.04)'
  };

  var validCount = rows.filter(isRowValid).length;

  return React.createElement('div', { className: 'fl', style: { gridColumn: '1 / -1' } },
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 } },
      React.createElement('label', null, 'Variants (Size / Price / Weight / Stock) *'),
      React.createElement('div', { style: { display: 'flex', gap: 8 } },
        React.createElement('button', {
          type: 'button',
          onClick: onAdd,
          style: {
            background: 'rgba(20,184,166,0.15)', border: '1px solid #14b8a6', color: '#14b8a6',
            borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            fontFamily: "'Outfit',sans-serif"
          }
        }, '+ Add Variant'),
        React.createElement('button', {
          type: 'button',
          onClick: onViewVariants,
          style: {
            background: 'rgba(123,44,255,0.14)', border: '1px solid #7B2CFF', color: '#C4B5FD',
            borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            fontFamily: "'Outfit',sans-serif", display: 'flex', alignItems: 'center', gap: 6
          }
        }, React.createElement(EyeIcon, null), 'View Variants (' + validCount + ')')
      )
    ),
    React.createElement('div', { style: { marginTop: 8 } },
      // Only the row currently being added/edited renders here — everything
      // else lives in the View Variants popup.
      rows.filter(function (row) { return expandedId === row.id; }).map(function (row) {
        return React.createElement('div', { key: row.id, style: cardStyle },
          React.createElement('div', null,
            React.createElement('span', { style: fieldLabel }, 'Size'),
            React.createElement('input', {
              type: 'text', placeholder: 'e.g. 1x1, 4x4, 6ft Standard', value: row.size, autoFocus: true,
              onChange: function (e) { onChange(row.id, 'size', e.target.value); }
            })
          ),
          React.createElement('div', null,
            React.createElement('span', { style: fieldLabel }, 'Price (\u20B9)'),
            React.createElement('input', {
              type: 'number', placeholder: '299', min: '0', step: '0.01', value: row.price,
              onChange: function (e) { onChange(row.id, 'price', e.target.value); }
            })
          ),
          React.createElement('div', null,
            React.createElement('span', { style: fieldLabel }, 'Weight (kg)'),
            React.createElement('input', {
              type: 'number', placeholder: '0.5', min: '0', step: '0.01', value: row.weight,
              onChange: function (e) { onChange(row.id, 'weight', e.target.value); }
            })
          ),
          React.createElement('div', null,
            React.createElement('span', { style: fieldLabel }, 'Stock'),
            React.createElement('input', {
              type: 'number', placeholder: '100', min: '0', value: row.stock,
              onChange: function (e) { onChange(row.id, 'stock', e.target.value); }
            })
          ),
          React.createElement('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', gridColumn: '1 / -1', marginTop: 4 } },
            rows.length > 1 && React.createElement('button', {
              type: 'button',
              onClick: function () { onRemove(row.id); },
              title: 'Remove variant',
              style: {
                background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.4)', color: '#f87171',
                cursor: 'pointer', fontSize: 13, lineHeight: 1, borderRadius: 6,
                padding: '10px 16px', fontFamily: "'Outfit',sans-serif", fontWeight: 600, whiteSpace: 'nowrap'
              }
            }, '\u2715 Remove'),
            React.createElement('button', {
              type: 'button',
              onClick: function () { onToggleExpand(row.id); },
              title: 'Save & collapse',
              style: {
                background: 'rgba(20,184,166,0.15)', border: '1px solid #14b8a6', color: '#14b8a6',
                cursor: 'pointer', fontSize: 13, lineHeight: 1, borderRadius: 6,
                padding: '10px 16px', fontFamily: "'Outfit',sans-serif", fontWeight: 600, whiteSpace: 'nowrap'
              }
            }, 'Done')
          )
        );
      })
    ),
    error && React.createElement('div', {
      style: { color: '#dc2626', fontSize: 12, marginTop: 4, fontFamily: "'Arial','Outfit',sans-serif" }
    }, error)
  );
};

// ── View Variants Modal ──────────────────────────────────────────────────────
// Shows every variant for the product being added/edited. Edit turns that
// one row into inline-editable inputs right here (Save/Cancel), and Delete
// removes it immediately. Same popup is reused for both Add and Edit flows.
var ViewVariantsModal = function (props) {
  var rows = props.rows;
  var onRemove = props.onRemove;
  var onChange = props.onChange;
  var onClose = props.onClose;
  var es = useState(null); var editingId = es[0]; var setEditingId = es[1];
  var ds = useState(null); var deletingId = ds[0]; var setDeletingId = ds[1];

  var fieldLabel = { fontSize: 10, fontWeight: 800, color: '#4c1d95', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, display: 'block' };
  var darkInput = {
    width: '100%', padding: '10px 12px', borderRadius: 10,
    border: '1px solid #e9d5ff', background: '#ffffff',
    color: '#1f2937', fontFamily: "'Outfit',sans-serif", fontSize: 13,
    outline: 'none', boxSizing: 'border-box'
  };

  return React.createElement('div', {
    onClick: function (e) { if (e.target === e.currentTarget) onClose(); },
    style: {
      position: 'fixed', inset: 0,
      background: 'rgba(17, 24, 39, 0.35)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2147483647, padding: 20,
    }
  },
    React.createElement('div', {
      style: {
        width: '100%', maxWidth: 520, maxHeight: '82vh', overflowY: 'auto',
        borderRadius: 24, background: '#f8f4ff', border: '2px solid #e9d5ff',
        padding: 24, boxShadow: '0 22px 70px rgba(109, 40, 217, 0.18)',
      }
    },
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 } },
        React.createElement('span', { style: { color: '#3b236a', fontFamily: "'Outfit',sans-serif", fontSize: 18, fontWeight: 800 } }, 'All Variants (' + rows.filter(isRowValid).length + ')'),
        React.createElement('button', {
          onClick: onClose, type: 'button',
          style: { background: '#fdf2f8', border: '1px solid #f9a8d4', color: '#9d174d', cursor: 'pointer', width: 32, height: 32, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700 }
        }, '×')
      ),

      rows.filter(isRowValid).length === 0
        ? React.createElement('div', { style: { color: '#64748b', textAlign: 'center', padding: '28px 0', fontFamily: "'Outfit',sans-serif" } }, 'No variants added yet. Close this and click "+ Add Variant".')
        : rows.filter(isRowValid).map(function (row) {
          var editing = editingId === row.id;

          if (editing) {
            return React.createElement('div', {
              key: row.id,
              style: {
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 10,
                alignItems: 'end', padding: 12, marginBottom: 10,
                border: '1px solid #d8b4fe', borderRadius: 14, background: '#ffffff'
              }
            },
              React.createElement('div', null,
                React.createElement('span', { style: fieldLabel }, 'Size'),
                React.createElement('input', { type: 'text', value: row.size, autoFocus: true, style: darkInput, onChange: function (e) { onChange(row.id, 'size', e.target.value); } })
              ),
              React.createElement('div', null,
                React.createElement('span', { style: fieldLabel }, 'Price (\u20B9)'),
                React.createElement('input', { type: 'number', min: '0', step: '0.01', value: row.price, style: darkInput, onChange: function (e) { onChange(row.id, 'price', e.target.value); } })
              ),
              React.createElement('div', null,
                React.createElement('span', { style: fieldLabel }, 'Weight (kg)'),
                React.createElement('input', { type: 'number', min: '0', step: '0.01', value: row.weight, style: darkInput, onChange: function (e) { onChange(row.id, 'weight', e.target.value); } })
              ),
              React.createElement('div', null,
                React.createElement('span', { style: fieldLabel }, 'Stock'),
                React.createElement('input', { type: 'number', min: '0', value: row.stock, style: darkInput, onChange: function (e) { onChange(row.id, 'stock', e.target.value); } })
              ),
              React.createElement('button', {
                type: 'button',
                onClick: function () { setEditingId(null); },
                style: {
                  gridColumn: '1 / -1', background: '#dcfce7', border: '1px solid #4ade80',
                  color: '#166534', borderRadius: 8, padding: '10px 12px', fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'Outfit',sans-serif"
                }
              }, 'Save')
            );
          }

          return React.createElement('div', {
            key: row.id,
            style: {
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              padding: '14px 16px', marginBottom: 10,
              border: '1px solid #e9d5ff', borderRadius: 16, background: '#ffffff',
              boxShadow: '0 10px 24px rgba(109, 40, 217, 0.06)'
            }
          },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flex: 1 } },
              React.createElement('span', { style: { fontWeight: 800, fontSize: 15, color: '#302559', minWidth: 68 } }, row.size),
              React.createElement('span', { style: { color: '#0f172a', fontWeight: 700, fontSize: 15 } }, '\u20B9', row.price),
              React.createElement('span', { style: { color: '#475569', fontSize: 13, fontWeight: 600 } }, row.weight, ' kg'),
              React.createElement('span', { style: { color: '#475569', fontSize: 13, fontWeight: 600 } }, 'Stock: ', row.stock)
            ),
            React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center' } },
              React.createElement('button', {
                type: 'button', title: 'Edit variant',
                onClick: function () { setEditingId(row.id); },
                style: {
                  background: '#dcfce7', border: '1px solid #4ade80', color: '#166534',
                  borderRadius: 8, padding: '8px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 700,
                  fontFamily: "'Outfit',sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4
                }
              }, React.createElement(PencilIcon, null), 'Edit'),
              React.createElement('button', {
                type: 'button', title: 'Delete variant',
                onClick: function () { setDeletingId(row.id); },
                style: {
                  background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c',
                  cursor: 'pointer', fontSize: 12, lineHeight: 1, borderRadius: 8,
                  padding: '8px 10px', fontFamily: "'Outfit',sans-serif", fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }
              }, '✕')
            )
          );
        }),

      React.createElement('button', {
        type: 'button', onClick: onClose,
        style: {
          marginTop: 14, width: '100%', padding: 12, borderRadius: 14,
          border: '2px solid #d8b4fe', background: '#ffffff', color: '#6d28d9',
          cursor: 'pointer', fontFamily: "'Outfit',sans-serif", fontWeight: 700, boxShadow: '0 10px 24px rgba(109, 40, 217, 0.08)'
        }
      }, 'Close')
    ),

    deletingId && React.createElement('div', {
      onClick: function (e) { if (e.target === e.currentTarget) setDeletingId(null); },
      style: {
        position: 'fixed', inset: 0, zIndex: 2147483647,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
      }
    },
      React.createElement('div', {
        style: {
          width: '100%', maxWidth: 340, borderRadius: 16,
          background: '#ffffff', border: '1px solid rgba(62,124,140,0.22)',
          padding: 26, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
        }
      },
        React.createElement('div', { style: { color: '#fff', fontFamily: "'Outfit',sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 8 } }, 'Delete this variant?'),
        React.createElement('div', { style: { color: '#94a3b8', fontFamily: "'Outfit',sans-serif", fontSize: 13, marginBottom: 20 } }, 'This cannot be undone.'),
        React.createElement('div', { style: { display: 'flex', gap: 10 } },
          React.createElement('button', {
            type: 'button',
            onClick: function () { setDeletingId(null); },
            style: {
              flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid rgba(62,124,140,0.28)',
              background: 'none', color: '#C4B5FD', cursor: 'pointer',
              fontFamily: "'Outfit',sans-serif", fontWeight: 600
            }
          }, 'Cancel'),
          React.createElement('button', {
            type: 'button',
            onClick: function () { onRemove(deletingId); setDeletingId(null); },
            style: {
              flex: 1, padding: '10px 0', borderRadius: 8, border: 'none',
              background: '#dc2626', color: '#fff', cursor: 'pointer',
              fontFamily: "'Outfit',sans-serif", fontWeight: 600
            }
          }, 'Delete')
        )
      )
    )
  );
};

var Products = function () {
  var location = useLocation();
  var navigate = useNavigate();
  var _perm = usePermission();
  var hasPermission = _perm.hasPermission;
  var canCreateProduct = hasPermission('products', 'create');
  var canEditProduct = hasPermission('products', 'edit');
  var canDeleteProduct = hasPermission('products', 'delete');
  var aQO = useState(false); var queryProductOpened = aQO[0]; var setQueryProductOpened = aQO[1];
  var queryProductIdInFlightRef = useRef('');
  var a1 = useState([]); var products = a1[0]; var setProducts = a1[1];
  var a2 = useState([]); var categories = a2[0]; var setCategories = a2[1];
  var a3 = useState([]); var subcategories = a3[0]; var setSubcategories = a3[1];
  var a4 = useState([]); var filteredSubs = a4[0]; var setFilteredSubs = a4[1];
  var a5 = useState(false); var showModal = a5[0]; var setShowModal = a5[1];
  var a6 = useState(null); var editId = a6[0]; var setEditId = a6[1];
  var a7 = useState(false); var loading = a7[0]; var setLoading = a7[1];
  var a8 = useState(false); var submitLoading = a8[0]; var setSubmitLoading = a8[1];
  var a9 = useState({ text: '', type: '' }); var msg = a9[0]; var setMsg = a9[1];
  var a10 = useState(1); var currentPage = a10[0]; var setCurrentPage = a10[1];
  var a11 = useState(null); var viewProduct = a11[0]; var setViewProduct = a11[1];
  var a12 = useState(''); var name = a12[0]; var setName = a12[1];
  var a13 = useState(''); var description = a13[0]; var setDescription = a13[1];
  var a34 = useState([makeEmptyVariant()]); var variants = a34[0]; var setVariants = a34[1];
  var a35 = useState(''); var variantsError = a35[0]; var setVariantsError = a35[1];
  var a36 = useState(variants[0] ? variants[0].id : null); var expandedVariantId = a36[0]; var setExpandedVariantId = a36[1];
  var a37 = useState(false); var showVariantsModal = a37[0]; var setShowVariantsModal = a37[1];
  var a16 = useState(''); var categoryId = a16[0]; var setCategoryId = a16[1];
  var a17 = useState(''); var subcategoryId = a17[0]; var setSubId = a17[1];
  var a18 = useState([]); var imageFiles = a18[0]; var setImageFiles = a18[1];
  var a19 = useState([]); var imagePreviews = a19[0]; var setImagePreviews = a19[1];
  var a20 = useState(null); var pendingDelete = a20[0]; var setPendingDelete = a20[1];
  var a21 = useState(''); var imageUrl = a21[0]; var setImageUrl = a21[1];
  var a22 = useState([]); var existingImages = a22[0]; var setExistingImages = a22[1];
  var a23 = useState(0); var primaryImageIndex = a23[0]; var setPrimaryImageIndex = a23[1];
  var a24 = useState(false); var showSizeWarn = a24[0]; var setShowSizeWarn = a24[1];
  var a25 = useState(false); var showTypeWarn = a25[0]; var setShowTypeWarn = a25[1];
  var a38 = useState(false); var showChooseModal = a38[0]; var setShowChooseModal = a38[1];
  var a39 = useState(0); var tempModalIndex = a39[0]; var setTempModalIndex = a39[1];
  var a29 = useState(''); var nameError = a29[0]; var setNameError = a29[1];
  var aImgErr = useState(''); var imageError = aImgErr[0]; var setImageError = aImgErr[1];
  var a30 = useState(null); var togglingId = a30[0]; var setTogglingId = a30[1];
  var a31 = useState('active'); var productStatus = a31[0]; var setProductStatus = a31[1];
  var aStatFilter = useState('all'); var statusFilter = aStatFilter[0]; var setStatusFilter = aStatFilter[1];
  var aSearch = useState(''); var search = aSearch[0]; var setSearch = aSearch[1];
  var aFromDate = useState(''); var fromDate = aFromDate[0]; var setFromDate = aFromDate[1];
  var aToDate = useState(''); var toDate = aToDate[0]; var setToDate = aToDate[1];
  var aSuccessModal = useState({ isOpen: false, message: '', subtitle: '' }); var successModal = aSuccessModal[0]; var setSuccessModal = aSuccessModal[1];
  var aDelSuccessModal = useState(null); var deleteSuccess = aDelSuccessModal[0]; var setDeleteSuccess = aDelSuccessModal[1];
  var fileInputRef = useRef();

  var getAllUniqueImages = function (existList, newPreviews) {
    var out = [];
    var seenKeys = {};

    (existList || []).forEach(function (src, idx) {
      var raw = sanitizeRawImagePath(src);
      var baseKey = getBaseCanonicalKey(raw || src);
      if (baseKey && !seenKeys[baseKey]) {
        seenKeys[baseKey] = true;
        out.push({
          id: 'ext-' + idx + '-' + baseKey,
          type: 'existing',
          indexInExisting: idx,
          src: resolveImageUrl(src),
          rawPath: raw,
          key: baseKey
        });
      }
    });

    (newPreviews || []).forEach(function (item, idx) {
      var baseKey = getBaseCanonicalKey(item);
      if (baseKey && !seenKeys[baseKey]) {
        seenKeys[baseKey] = true;
        out.push({
          id: 'new-' + idx + '-' + baseKey,
          type: 'new',
          indexInNew: idx,
          src: item.preview,
          file: item.file,
          key: baseKey
        });
      }
    });

    return out;
  };

  var handleRemoveImageItem = function (item) {
    var all = getAllUniqueImages(existingImages, imagePreviews);
    var itemIdx = all.findIndex(function (i) { return i.key === item.key; });

    if (item.type === 'existing') {
      setExistingImages(function (prev) {
        return prev.filter(function (src) {
          return getBaseCanonicalKey(src) !== item.key;
        });
      });
    } else if (item.type === 'new') {
      var targetPreview = imagePreviews.find(function (p) {
        return getBaseCanonicalKey(p) === item.key;
      });
      if (targetPreview && targetPreview.preview && targetPreview.preview.indexOf('blob:') === 0) {
        URL.revokeObjectURL(targetPreview.preview);
      }
      setImagePreviews(function (prev) {
        return prev.filter(function (p) {
          return getBaseCanonicalKey(p) !== item.key;
        });
      });
      setImageFiles(function (prev) {
        return prev.filter(function (f) {
          return getBaseCanonicalKey(f) !== item.key;
        });
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
    }

    setPrimaryImageIndex(function (prevIdx) {
      if (itemIdx === prevIdx) {
        return 0;
      } else if (itemIdx < prevIdx) {
        return Math.max(0, prevIdx - 1);
      }
      return prevIdx;
    });
  };

  var validateStockQty = function (val) {
    if (val === '' || val === null || val === undefined) {
      return 'Stock quantity is required';
    }
    var trimmed = String(val).trim();
    if (!trimmed) {
      return 'Stock quantity is required';
    }
    if (trimmed.match(/[^0-9]/)) {
      return 'Stock quantity must contain only numbers';
    }
    var numVal = parseInt(trimmed, 10);
    if (isNaN(numVal)) {
      return 'Stock quantity must be a number';
    }
    if (numVal <= 0) {
      return 'Stock quantity must be greater than 0';
    }
    return '';
  };

  var validateWeight = function (val) {
    if (val === '' || val === null || val === undefined) {
      return 'Weight is required';
    }
    var trimmed = String(val).trim();
    if (!trimmed) {
      return 'Weight is required';
    }
    var numVal = parseFloat(trimmed);
    if (isNaN(numVal)) {
      return 'Weight must be a number';
    }
    if (numVal <= 0) {
      return 'Weight must be greater than 0';
    }
    return '';
  };

  var addVariantRow = function () {
    var newRow = makeEmptyVariant();
    setVariants(function (prev) { return prev.concat([newRow]); });
    // Auto-expand the freshly added row so its inline form opens right away.
    setExpandedVariantId(newRow.id);
  };
  var removeVariantRow = function (id) {
    setVariants(function (prev) {
      var next = prev.filter(function (v) { return v.id !== id; });
      return next.length ? next : [makeEmptyVariant()];
    });
    setExpandedVariantId(function (prevExpanded) {
      return prevExpanded === id ? null : prevExpanded;
    });
  };
  var updateVariantField = function (id, field, value) {
    setVariants(function (prev) {
      return prev.map(function (v) {
        return v.id === id ? Object.assign({}, v, { [field]: value }) : v;
      });
    });
    if (variantsError) setVariantsError('');
  };
  var toggleExpandVariant = function (id) {
    setExpandedVariantId(function (prev) { return prev === id ? null : id; });
  };

  var validateVariants = function (list) {
    if (!list || !list.length) return 'Add at least one variant (size, price, weight, quantity)';
    var sizesSeen = {};
    for (var i = 0; i < list.length; i++) {
      var v = list[i];
      var rowNum = i + 1;
      var size = (v.size || '').trim();
      if (!size) return 'Variant ' + rowNum + ': size is required';
      if (sizesSeen[size.toLowerCase()]) return 'Variant ' + rowNum + ': size "' + size + '" is repeated';
      sizesSeen[size.toLowerCase()] = true;

      if (v.price === '' || v.price === null || v.price === undefined || isNaN(Number(v.price)) || Number(v.price) < 0) {
        return 'Variant ' + rowNum + ' (' + size + '): enter a valid price';
      }
      if (v.weight === '' || v.weight === null || v.weight === undefined || isNaN(Number(v.weight)) || Number(v.weight) < 0) {
        return 'Variant ' + rowNum + ' (' + size + '): enter a valid weight';
      }
      if (v.stock === '' || v.stock === null || v.stock === undefined || isNaN(Number(v.stock)) || Number(v.stock) < 0) {
        return 'Variant ' + rowNum + ' (' + size + '): enter a valid quantity';
      }
    }
    return '';
  };

  var showMsg = function (text, type) {
    setMsg({ text: text, type: type });
    setTimeout(function () { setMsg({ text: '', type: '' }); }, 5000);
  };

  var getErrorStatus = function (err) {
    return err && err.response && err.response.status;
  };

  var clearImage = function () {
    imagePreviews.forEach(function (img) { if (img && img.preview && img.preview.indexOf('blob:') === 0) URL.revokeObjectURL(img.preview); });
    setImagePreviews([]); setImageFiles([]); setExistingImages([]); setImageUrl(''); setPrimaryImageIndex(0); setShowChooseModal(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  var removeExistingImage = function (index) {
    setExistingImages(function (prev) {
      var itemToRemove = prev[index];
      if (!itemToRemove) return prev;
      var k = getBaseCanonicalKey(itemToRemove);
      return prev.filter(function (src) { return getBaseCanonicalKey(src) !== k; });
    });
    setPrimaryImageIndex(function (prev) { return prev > index ? prev - 1 : 0; });
  };
  var removeNewImage = function (index) {
    var item = imagePreviews[index];
    if (item) {
      var k = getBaseCanonicalKey(item);
      if (item.preview && item.preview.indexOf('blob:') === 0) URL.revokeObjectURL(item.preview);
      setImagePreviews(function (prev) { return prev.filter(function (p) { return getBaseCanonicalKey(p) !== k; }); });
      setImageFiles(function (prev) { return prev.filter(function (f) { return getBaseCanonicalKey(f) !== k; }); });
    }
    setPrimaryImageIndex(function (prev) { return prev > existingImages.length + index ? prev - 1 : 0; });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  var resetForm = function () {
    var initial = [makeEmptyVariant()];
    setName(''); setDescription(''); setVariants(initial); setVariantsError('');
    setExpandedVariantId(initial[0].id);
    setShowVariantsModal(false);
    setCategoryId(''); setSubId(''); setFilteredSubs([]); setEditId(null);
    clearImage(); setNameError(''); setImageError(''); setProductStatus('active');
  };
  var closeModal = function () {
    setShowModal(false); resetForm(); setMsg({ text: '', type: '' });
  };
  var handleImageChange = function (e) {
    var files = Array.prototype.slice.call((e.target && e.target.files) || []);
    if (!files.length) return;

    var currentAll = getAllUniqueImages(existingImages, imagePreviews);
    var seenBaseKeys = {};
    currentAll.forEach(function (item) {
      if (item.key) seenBaseKeys[item.key] = true;
    });

    var validFiles = [];
    var newPreviewsToCreate = [];

    for (var i = 0; i < files.length; i++) {
      var file = files[i];
      if (file.type !== 'image/jpeg' && file.type !== 'image/png' && file.type !== 'image/webp') {
        if (fileInputRef.current) fileInputRef.current.value = '';
        setShowTypeWarn(true);
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        if (fileInputRef.current) fileInputRef.current.value = '';
        setShowSizeWarn(true);
        return;
      }
      var fBaseKey = getBaseCanonicalKey(file);
      if (fBaseKey && seenBaseKeys[fBaseKey]) {
        continue;
      }
      if (fBaseKey) {
        seenBaseKeys[fBaseKey] = true;
      }
      validFiles.push(file);
      newPreviewsToCreate.push({
        file: file,
        preview: URL.createObjectURL(file),
        name: file.name
      });
    }

    if (validFiles.length > 0) {
      setImageFiles(function (prev) {
        var existingKeys = {};
        prev.forEach(function (f) {
          var k = getBaseCanonicalKey(f);
          if (k) existingKeys[k] = true;
        });
        var toAdd = validFiles.filter(function (f) {
          var k = getBaseCanonicalKey(f);
          return k && !existingKeys[k];
        });
        return prev.concat(toAdd);
      });

      setImagePreviews(function (prev) {
        var existingKeys = {};
        prev.forEach(function (p) {
          var k = getBaseCanonicalKey(p);
          if (k) existingKeys[k] = true;
        });
        var toAdd = newPreviewsToCreate.filter(function (p) {
          var k = getBaseCanonicalKey(p);
          if (k && existingKeys[k]) {
            if (p.preview && p.preview.indexOf('blob:') === 0) {
              URL.revokeObjectURL(p.preview);
            }
            return false;
          }
          return true;
        });
        return prev.concat(toAdd);
      });
    }

    setImageUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  var handleImageDrop = function (e) {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    if (e.dataTransfer.files && e.dataTransfer.files.length) handleImageChange({ target: { files: e.dataTransfer.files } });
  };

  var handleImageUrlChange = function (e) {
    var val = e.target.value || '';
    setImageUrl(val);
    if (val.trim()) {
      var trimmed = val.trim();
      var key = getBaseCanonicalKey(trimmed);
      var currentAll = getAllUniqueImages(existingImages, imagePreviews);
      var exists = currentAll.some(function (item) { return item.key === key; });
      if (!exists) {
        setExistingImages(function (prev) {
          var prevKeys = {};
          prev.forEach(function (img) {
            var k = getBaseCanonicalKey(img);
            if (k) prevKeys[k] = true;
          });
          if (!prevKeys[key]) {
            return prev.concat([trimmed]);
          }
          return prev;
        });
      }
      setImageUrl('');
    }
  };


  var fetchProducts = function () {
    setLoading(true);
    getAllProductsUnpaginated()
      .then(function (res) {
        var prods = extractArray(res);
        if (prods && prods.length > 0) {
          setProducts(prods);
        } else {
          return getAllProducts(1, 10000).then(function (res2) {
            setProducts(extractArray(res2));
          });
        }
      })
      .catch(function () {
        return getAllProducts(1, 10000).then(function (res2) {
          setProducts(extractArray(res2));
        });
      })
      .catch(function (err) {
        console.error('Fetch error:', err);
        showMsg('Failed to load data', 'e');
      })
      .finally(function () { setLoading(false); });
  };

  var fetchLookups = function () {
    Promise.all([getAllCategories(), getAllSubcategories()])
      .then(function (results) {
        setCategories(extractArray(results[0]));
        setSubcategories(extractArray(results[1]));
      })
      .catch(function (err) { console.error('Lookup fetch error:', err); });
  };

  var fetchData = function () {
    fetchProducts();
    fetchLookups();
  };

  useEffect(function () {
    fetchData();
  }, []); // eslint-disable-line

  useEffect(function () {
    setQueryProductOpened(false);
    queryProductIdInFlightRef.current = '';
  }, [location.search]);

  useEffect(function () {
    if (queryProductOpened) return;
    var productId = new URLSearchParams(location.search).get('productId');
    if (!productId) return;
    if (queryProductIdInFlightRef.current === productId) return;
    queryProductIdInFlightRef.current = productId;
    // `products` now only holds the current page, not the whole catalog, so
    // a deep link (e.g. from a notification) fetches the product directly
    // instead of searching the loaded page for it.
    getProductById(productId)
      .then(function (res) {
        var d = res && res.data;
        var prod = (d && d.data) || d;
        if (prod) setViewProduct(prod);
      })
      .catch(function (err) {
        if (getErrorStatus(err) === 404) {
          showMsg('That product is no longer available.', 'e');
          return;
        }
        console.error('Deep-link product fetch error:', err);
        showMsg('Could not load product details.', 'e');
      })
      .finally(function () {
        setQueryProductOpened(true);
        queryProductIdInFlightRef.current = '';
        navigate(location.pathname, { replace: true });
      });
  }, [location.search, queryProductOpened, location.pathname, navigate]);

  var handleCategoryChange = function (catId) {
    setCategoryId(catId);
    setSubId('');
    if (catId) {
      var filtered = subcategories.filter(function (sub) {
        var subCatId = resolveId(sub.category) || resolveId(sub.categoryId);
        return subCatId === catId;
      });
      setFilteredSubs(filtered.length > 0 ? filtered : subcategories);
    } else {
      setFilteredSubs([]);
    }
  };

  var handleSubmit = async function () {
    if (!hasPermission('products', editId ? 'edit' : 'create')) { showMsg('You do not have permission to save products.', 'e'); return; }
    var trimmedName = name.trim();
    var nameErr = validateProductName(trimmedName);
    if (nameErr) {
      setNameError(nameErr);
      showMsg(nameErr, 'e');
      return;
    }
    setNameError('');

    var variantsErr = validateVariants(variants);
    if (variantsErr) { setVariantsError(variantsErr); showMsg(variantsErr, 'e'); return; }
    setVariantsError('');

    if (!categoryId) { showMsg('Please select a category', 'e'); return; }
    if (!subcategoryId) { showMsg('Please select a subcategory', 'e'); return; }

    var allUniqueImages = getAllUniqueImages(existingImages, imagePreviews);
    if (!allUniqueImages || allUniqueImages.length === 0) {
      setImageError('Product image is mandatory. Please upload at least one image.');
      showMsg('Product image is mandatory. Please upload at least one image.', 'e');
      return;
    }
    setImageError('');

    setSubmitLoading(true);
    var variantsPayload = variants.map(function (v) {
      return {
        size: v.size.trim(),
        price: Number(v.price),
        weight: Number(v.weight),
        stock: Number(v.stock)
      };
    });
    var variantsJSON = JSON.stringify(variantsPayload);
    var targetMainItem = allUniqueImages[primaryImageIndex] || allUniqueImages[0];

    var uniqueExistingItems = allUniqueImages.filter(function (i) { return i.type === 'existing'; });
    var orderedExistingImages = [];
    var seenExistKeys = {};
    uniqueExistingItems.forEach(function (i) {
      var raw = i.rawPath;
      var k = getBaseCanonicalKey(raw);
      if (raw && k && !seenExistKeys[k]) {
        seenExistKeys[k] = true;
        orderedExistingImages.push(raw);
      }
    });

    var uniqueNewItems = allUniqueImages.filter(function (i) { return i.type === 'new'; });
    var orderedImageFiles = [];
    var seenFileKeys = {};
    uniqueNewItems.forEach(function (i) {
      var file = i.file;
      var k = getBaseCanonicalKey(file);
      if (file && k && !seenFileKeys[k]) {
        seenFileKeys[k] = true;
        orderedImageFiles.push(file);
      }
    });

    if (targetMainItem && targetMainItem.type === 'existing') {
      var mainRaw = targetMainItem.rawPath;
      var mainK = getBaseCanonicalKey(mainRaw);
      var eIdx = orderedExistingImages.findIndex(function (r) { return getBaseCanonicalKey(r) === mainK; });
      if (eIdx > 0) {
        var removedRaw = orderedExistingImages.splice(eIdx, 1)[0];
        orderedExistingImages.unshift(removedRaw);
      }
    }

    if (targetMainItem && targetMainItem.type === 'new') {
      var mainFile = targetMainItem.file;
      var mainK = getBaseCanonicalKey(mainFile);
      var nIdx = orderedImageFiles.findIndex(function (f) { return getBaseCanonicalKey(f) === mainK; });
      if (nIdx > 0) {
        var removedFile = orderedImageFiles.splice(nIdx, 1)[0];
        orderedImageFiles.unshift(removedFile);
      }
    }

    var onSuccess = function () {
      closeModal();
      if (editId) {
        setSuccessModal({
          isOpen: true,
          message: 'Product updated successfully!',
          subtitle: 'Your changes have been saved.'
        });
      } else {
        setSuccessModal({
          isOpen: true,
          message: 'Product added successfully!',
          subtitle: 'The new product is now available.'
        });
      }
      fetchData();
      window.dispatchEvent(new Event('adminDataUpdated'));
      setSubmitLoading(false);
    };
    var onFail = function (err) {
      var errMsg = (err.response && err.response.data &&
        (err.response.data.message || err.response.data.msg)) || 'Failed to save product';
      showMsg(errMsg, 'e');
      setSubmitLoading(false);
    };

    try {
      var fd = new FormData();
      fd.append('name', trimmedName);
      fd.append('description', description.trim());
      fd.append('variants', variantsJSON);
      fd.append('category', categoryId);
      fd.append('categoryId', categoryId);
      fd.append('subcategory', subcategoryId);
      fd.append('subcategoryId', subcategoryId);
      fd.append('status', productStatus);

      if (editId || existingImages.length || imageFiles.length) fd.append('existingImages', JSON.stringify(orderedExistingImages));
      orderedImageFiles.forEach(function (file) { fd.append('images', file); });
      if (orderedImageFiles[0]) fd.append('image', orderedImageFiles[0]);
      if (editId && orderedExistingImages.length === 0 && orderedImageFiles.length === 0) fd.append('removeImage', 'true');

      await (editId ? updateProduct(editId, fd) : createProduct(fd));
      onSuccess();
    } catch (err) {
      console.error('[Products] save error:', (err.response && err.response.data) || err);
      onFail(err);
    }
  };

  var handleEdit = function (pro) {
    if (!canEditProduct) { showMsg('You do not have permission to edit products.', 'e'); return; }
    var catId = resolveId(pro.category) || resolveId(pro.categoryId) || '';
    var subId = resolveId(pro.subcategory) || resolveId(pro.subcategoryId) || '';

    setEditId(pro._id || pro.id);
    setName(pro.name || '');
    setDescription(pro.description || '');
    var loadedVariants;
    if (Array.isArray(pro.variants) && pro.variants.length > 0) {
      loadedVariants = pro.variants.map(function (v) {
        variantRowId += 1;
        return {
          id: 'v' + variantRowId,
          size: v.size !== undefined && v.size !== null ? String(v.size) : '',
          price: v.price !== undefined && v.price !== null ? String(v.price) : '',
          weight: v.weight !== undefined && v.weight !== null ? String(v.weight) : '',
          stock: v.stock !== undefined && v.stock !== null ? String(v.stock) : ''
        };
      });
    } else {
      variantRowId += 1;
      loadedVariants = [{
        id: 'v' + variantRowId,
        size: 'Standard',
        price: pro.price !== undefined && pro.price !== null ? String(pro.price) : '',
        weight: pro.weight !== undefined && pro.weight !== null ? String(pro.weight) : '',
        stock: (function () { var sv = getStock(pro); return sv !== null && sv !== undefined ? String(sv) : ''; })()
      }];
    }
    setVariants(loadedVariants);
    setExpandedVariantId(null);
    setShowVariantsModal(false);
    setVariantsError('');
    setCategoryId(catId);
    setSubId(subId);
    setProductStatus((pro.status || 'active').toLowerCase());

    var savedImages = extractImageList(pro, false);
    var uniqueSavedImages = [];
    var seenSaved = {};
    savedImages.forEach(function (img) {
      var clean = sanitizeRawImagePath(img);
      var key = getBaseCanonicalKey(clean || img);
      if (clean && key && !seenSaved[key]) {
        seenSaved[key] = true;
        uniqueSavedImages.push(clean);
      }
    });
    setExistingImages(uniqueSavedImages);
    setImageUrl('');
    setImagePreviews([]);
    setImageFiles([]);
    setPrimaryImageIndex(0);

    if (catId) {
      var filtered = subcategories.filter(function (sub) {
        return (resolveId(sub.category) || resolveId(sub.categoryId)) === catId;
      });
      setFilteredSubs(filtered.length > 0 ? filtered : subcategories);
    }

    setShowModal(true);
  };

  var handleDelete = function (productId) {
    if (!canDeleteProduct) { showMsg('You do not have permission to delete products.', 'e'); return; }
    if (!productId) { showMsg('Cannot delete: missing product ID', 'e'); return; }
    deleteProduct(productId)
      .then(function () {
        setDeleteSuccess({ id: productId });
      })
      .catch(function (err) {
        showMsg((err.response && err.response.data && (err.response.data.msg || err.response.data.message)) || 'Delete failed', 'e');
      });
  };

  var handleDeleteSuccessClose = function () {
    var id = deleteSuccess && deleteSuccess.id;
    setDeleteSuccess(null);
    if (id) {
      setProducts(function (prev) { return prev.filter(function (p) { return (p._id || p.id) !== id; }); });
    }
    fetchData();
    window.dispatchEvent(new Event('adminDataUpdated'));
  };

  var handleToggleStatus = function (productId, currentStatus) {
    if (!canEditProduct) { showMsg('You do not have permission to edit products.', 'e'); return; }
    if (!productId) return;
    var newStatus = (currentStatus || 'active').toLowerCase() === 'inactive' ? 'active' : 'inactive';

    setTogglingId(productId);
    setProducts(function (prev) {
      return prev.map(function (p) {
        return (p._id || p.id) === productId ? Object.assign({}, p, { status: newStatus }) : p;
      });
    });

    toggleProductStatus(productId)
      .then(function () {
        showMsg('Product marked as ' + newStatus + '! \u2705', 's');
        window.dispatchEvent(new Event('adminDataUpdated'));
      })
      .catch(function (err) {
        showMsg((err.response && err.response.data && (err.response.data.message || err.response.data.msg)) || 'Failed to update status', 'e');
        fetchData();
      })
      .finally(function () { setTogglingId(null); });
  };

  var requestDelete = function (product) {
    if (!canDeleteProduct) { showMsg('You do not have permission to delete products.', 'e'); return; }
    if (!product) { showMsg('Cannot delete: invalid product', 'e'); return; }
    setPendingDelete(product);
  };

  var cancelDelete = function () { setPendingDelete(null); };

  var confirmDelete = function () {
    if (!pendingDelete) return;
    var productId = pendingDelete._id || pendingDelete.id;
    setPendingDelete(null);
    handleDelete(productId);
  };
  var stockDisplay = function (p) {
    var sq = getStock(p);
    if (sq === null || sq === undefined) return { label: '\u2014', cls: 'stock-na' };
    if (sq === 0) return { label: '0', cls: 'stock-zero' };
    if (sq <= (p.low_stock_threshold || 5)) return { label: sq, cls: 'stock-low' };
    return { label: sq, cls: 'stock-ok' };
  };

  var handleInlineView = function (id) {
    var rowFallback = products.find(function (p) { return p._id === id || p.id === id; });
    if (rowFallback) setViewProduct(rowFallback);
    getProductById(id)
      .then(function (res) {
        var d = res && res.data;
        var prod = (d && (d.data || d)) || null;
        if (prod && (prod._id || prod.id)) setViewProduct(prod);
      })
      .catch(function (err) { console.error('[Products] view fetch error:', err); });
  };

  var handleInlineEdit = function (id) {
    if (!canEditProduct) { showMsg('You do not have permission to edit products.', 'e'); return; }
    var rowFallback = products.find(function (p) { return p._id === id || p.id === id; });
    getProductById(id)
      .then(function (res) {
        var d = res && res.data;
        var prod = (d && (d.data || d)) || null;
        if (prod && (prod._id || prod.id)) handleEdit(prod);
        else if (rowFallback) handleEdit(rowFallback);
      })
      .catch(function (err) {
        console.error('[Products] edit fetch error:', err);
        showMsg('Could not load full product details - description may be out of date', 'e');
        if (rowFallback) handleEdit(rowFallback);
      });
  };
  var handleStatCardClick = function (filterVal) {
    setStatusFilter(filterVal);
    setCurrentPage(1);
  };

  var totalProductsCount = products.length;
  var activeProductsCount = products.filter(isActiveStatus).length;
  var inactiveProductsCount = products.filter(function (p) { return !isActiveStatus(p); }).length;
  var outOfStockProductsCount = products.filter(function (p) { var stock = getStock(p); return stock !== null && Number(stock) <= 0; }).length;
  var productStatCards = [
    { title: 'Total Products', value: totalProductsCount, description: 'Across the catalog', icon: 'box', accent: '#d99a2b', active: statusFilter === 'all', onClick: function () { setStatusFilter('all'); setCurrentPage(1); } },
    { title: 'Active Products', value: activeProductsCount, description: 'Visible on catalog', icon: 'check', accent: '#10b981', active: statusFilter === 'active', onClick: function () { handleStatCardClick('active'); } },
    { title: 'Inactive Products', value: inactiveProductsCount, description: 'Hidden from catalog', icon: 'eyeOff', accent: '#6366f1', active: statusFilter === 'inactive', onClick: function () { handleStatCardClick('inactive'); } },
    { title: 'Out of Stock', value: outOfStockProductsCount, description: 'Needs restocking', icon: 'alert', accent: '#f59e0b', active: statusFilter === 'out_of_stock', onClick: function () { handleStatCardClick('out_of_stock'); } }
  ];

  var filteredProducts = products.filter(function (p) {
    if (statusFilter === 'active' && !isActiveStatus(p)) return false;
    if (statusFilter === 'inactive' && isActiveStatus(p)) return false;
    if (statusFilter === 'out_of_stock') {
      var stock = getStock(p);
      if (stock === null || Number(stock) > 0) return false;
    }

    if (search && search.trim()) {
      var q = search.trim().toLowerCase();
      var pName = (p.name || '').toLowerCase();
      var catName = (resolveName(p.category, categories) || resolveName(p.categoryId, categories) || '').toLowerCase();
      var subName = (resolveName(p.subcategory, subcategories) || resolveName(p.subcategoryId, subcategories) || '').toLowerCase();
      var desc = stripHtml(p.description || '').toLowerCase();
      var matched = pName.includes(q) || catName.includes(q) || subName.includes(q) || desc.includes(q);
      if (!matched) return false;
    }

    var pDate = p.createdAt || p.created_at || p.date || p.updatedAt || p.updated_at;
    if (fromDate || toDate) {
      if (!pDate) return false;
      var dStr = dateInputValue(pDate);
      if (fromDate && dStr < fromDate) return false;
      if (toDate && dStr > toDate) return false;
    }

    return true;
  });

  var totalFiltered = filteredProducts.length;
  var totalPages = Math.max(1, Math.ceil(totalFiltered / ITEMS_PER_PAGE));
  var pageItems = filteredProducts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  var subsToShow = filteredSubs.length > 0 ? filteredSubs : (categoryId ? subcategories : []);

  var thumbStyle = {
    width: 38, height: 38, borderRadius: 8, overflow: 'hidden',
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  };

  return React.createElement(AdminLayout, null,
    successModal.isOpen && React.createElement(UpdateSuccessModal, {
      isOpen: true,
      message: successModal.message,
      subtitle: successModal.subtitle,
      onClose: function () { setSuccessModal({ isOpen: false, message: '', subtitle: '' }); }
    }),

    showSizeWarn && React.createElement(ImageSizeWarningModal, {
      onClose: function () { setShowSizeWarn(false); }
    }),

    showTypeWarn && React.createElement(ImageTypeWarningModal, {
      onClose: function () { setShowTypeWarn(false); }
    }),

    showChooseModal && React.createElement(ChooseImageModal, {
      allImages: getAllUniqueImages(existingImages, imagePreviews),
      selectedIndex: tempModalIndex,
      onSelectIndex: setTempModalIndex,
      onConfirm: function () {
        setPrimaryImageIndex(tempModalIndex);
        setShowChooseModal(false);
      },
      onClose: function () { setShowChooseModal(false); }
    }),

    showVariantsModal && React.createElement(ViewVariantsModal, {
      rows: variants,
      onRemove: removeVariantRow,
      onChange: updateVariantField,
      onClose: function () { setShowVariantsModal(false); }
    }),

    viewProduct && React.createElement('div', {
      className: 'modal-overlay',
      onClick: function (e) { if (e.target.classList.contains('modal-overlay')) setViewProduct(null); }
    },
      React.createElement('div', { className: 'view-modal-box' },
        React.createElement('div', { className: 'modal-hdr' },
          React.createElement('span', { className: 'modal-title' }, 'Product Details'),
          React.createElement('button', { className: 'modal-close', onClick: function () { setViewProduct(null); } }, React.createElement(XIcon, null))
        ),
        React.createElement('div', { className: 'view-img-wrap product-gallery-view' },
          (function () {
            var imgs = extractImageList(viewProduct, true);

            if (!imgs.length) {
              return React.createElement('div', { style: { width: '100%', textAlign: 'center', padding: '28px 0', color: '#475569' } },
                React.createElement('svg', { width: 48, height: 48, viewBox: '0 0 24 24', fill: 'none', stroke: '#475569', strokeWidth: 1 },
                  React.createElement('rect', { x: 3, y: 3, width: 18, height: 18, rx: 3 }),
                  React.createElement('circle', { cx: 8.5, cy: 8.5, r: 1.5 }),
                  React.createElement('polyline', { points: '21 15 16 10 5 21' })
                ),
                React.createElement('div', { style: { fontSize: 11, marginTop: 6 } }, 'No image')
              );
            }
            return React.createElement('div', { className: 'product-view-gallery' },
              imgs.map(function (src, i) {
                return React.createElement('img', {
                  key: src + i,
                  src: src,
                  alt: (viewProduct.name || 'product') + ' image ' + (i + 1),
                  onError: function (e) { handleImgError(e, src); }
                });
              })
            );
          })()
        ),
        React.createElement('div', { className: 'view-grid' },
          React.createElement('div', { className: 'view-row' },
            React.createElement('span', { className: 'view-label' }, 'Name'),
            React.createElement('span', { className: 'view-val' }, viewProduct.name)
          ),
          React.createElement('div', { className: 'view-row view-full' },
            React.createElement('span', { className: 'view-label' }, 'Variants'),
            React.createElement('div', { className: 'view-val', style: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 } },
              (Array.isArray(viewProduct.variants) && viewProduct.variants.length > 0)
                ? viewProduct.variants.map(function (v, i) {
                  return React.createElement('div', { key: i, style: { display: 'flex', gap: 14, fontSize: 13 } },
                    React.createElement('span', { style: { minWidth: 70, fontWeight: 600 } }, v.size),
                    React.createElement('span', { style: { color: '#34d399', fontWeight: 700 } }, '\u20B9', v.price),
                    React.createElement('span', { style: { color: '#94a3b8' } }, v.weight, ' kg'),
                    React.createElement('span', { style: v.stock <= 5 ? { color: '#f87171' } : { color: '#94a3b8' } }, 'Stock: ', v.stock)
                  );
                })
                : React.createElement('span', null, '\u2014')
            )
          ),
          React.createElement('div', { className: 'view-row' },
            React.createElement('span', { className: 'view-label' }, 'Status'),
            React.createElement('span', { className: 'bdg ' + ((viewProduct.status || '').toLowerCase() === 'inactive' ? 'bdg-b' : 'bdg-g') }, viewProduct.status || 'active')
          ),
          React.createElement('div', { className: 'view-row' },
            React.createElement('span', { className: 'view-label' }, 'Category'),
            React.createElement('span', { className: 'view-val' }, resolveName(viewProduct.category, categories))
          ),
          React.createElement('div', { className: 'view-row' },
            React.createElement('span', { className: 'view-label' }, 'Subcategory'),
            React.createElement('span', { className: 'view-val' }, resolveName(viewProduct.subcategory, subcategories))
          ),
          React.createElement('div', { className: 'view-row view-full' },
            React.createElement('span', { className: 'view-label' }, 'Description'),
            viewProduct.description
              ? React.createElement('div', {
                className: 'view-val',
                style: { color: '#ffffff' },
                dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(viewProduct.description) }
              })
              : React.createElement('span', { className: 'view-val', style: { color: '#475569' } }, '\u2014')
          )
        ),
        React.createElement('div', { className: 'fa' },
          React.createElement('button', { className: 'btn btn-g', onClick: function () { setViewProduct(null); } }, 'Close')
        )
      )
    ),

    showModal && React.createElement('div', {
      className: 'modal-overlay',
      onClick: function (e) { if (e.target.classList.contains('modal-overlay')) closeModal(); }
    },
      React.createElement('div', { className: 'modal-box' },
        React.createElement('div', { className: 'modal-hdr' },
          React.createElement('span', { className: 'modal-title' }, editId ? 'Edit Product' : 'Add New Product'),
          React.createElement('button', { className: 'modal-close', onClick: closeModal }, React.createElement(XIcon, null))
        ),

        msg.text && React.createElement('div', { className: 'mbx ' + (msg.type === 'e' ? 'mbx-e' : 'mbx-s') }, msg.text),

        React.createElement('div', { className: 'fl modal-full' },
          React.createElement('label', null, 'Product Image *'),
          imageError && React.createElement('div', {
            style: { color: '#ef4444', fontSize: 12, marginTop: 2, marginBottom: 6, fontFamily: "'Arial','Outfit',sans-serif", fontWeight: 600 }
          }, imageError),
          React.createElement('div', { style: { fontSize: 11, color: '#64748b', marginBottom: 6, fontFamily: "'Outfit',sans-serif" } },
            '\u26A0\uFE0F Max image size: 3.4 MB (PNG, JPG, WEBP)'
          ),
          (function () {
            var allUnique = getAllUniqueImages(existingImages, imagePreviews);
            if (!allUnique.length) {
              return React.createElement('div', {
                className: 'img-upload-zone',
                onClick: function () { if (fileInputRef.current) fileInputRef.current.click(); },
                onDragOver: function (e) { e.preventDefault(); e.currentTarget.classList.add('drag-over'); },
                onDragLeave: function (e) { e.currentTarget.classList.remove('drag-over'); },
                onDrop: handleImageDrop
              },
                React.createElement('input', { type: 'file', accept: 'image/jpeg,image/png,image/webp', multiple: true, ref: fileInputRef, style: { display: 'none' }, onChange: handleImageChange }),
                React.createElement('span', { style: { color: '#3E7C8C' } }, React.createElement(UploadIcon, null)),
                React.createElement('span', { className: 'img-upload-hint' }, React.createElement('strong', null, 'Click to upload'), ' or drag & drop', React.createElement('br', null), 'PNG, JPG, WEBP - max 3.4 MB')
              );
            }

            var safePrimaryIdx = (primaryImageIndex >= 0 && primaryImageIndex < allUnique.length) ? primaryImageIndex : 0;
            var mainItem = allUnique[safePrimaryIdx] || allUnique[0];
            var mainCanonicalKey = mainItem ? mainItem.key : '';

            var optionalItems = allUnique.filter(function (item) {
              return item.key !== mainCanonicalKey;
            });

            return React.createElement('div', { className: 'product-images-layout' },
              React.createElement('input', { type: 'file', accept: 'image/jpeg,image/png,image/webp', multiple: true, ref: fileInputRef, style: { display: 'none' }, onChange: handleImageChange }),

              React.createElement('div', { className: 'main-image-card-wrap' },
                React.createElement('div', { className: 'main-image-card' },
                  React.createElement('div', { className: 'main-image-preview-container' },
                    React.createElement('span', { className: 'main-img-badge' }, 'Main Image'),
                    React.createElement('img', {
                      src: mainItem.src,
                      alt: 'Main product image',
                      onError: function (e) { if (mainItem.rawPath) handleImgError(e, mainItem.rawPath); }
                    })
                  ),
                  React.createElement('button', {
                    type: 'button',
                    className: 'choose-image-btn',
                    onClick: function () {
                      setTempModalIndex(safePrimaryIdx);
                      setShowChooseModal(true);
                    }
                  }, React.createElement(RefreshIcon, null), 'Choose Image')
                )
              ),

              React.createElement('div', { className: 'optional-images-section' },
                React.createElement('span', { className: 'optional-images-title' }, 'OPTIONAL IMAGES'),
                React.createElement('div', { className: 'optional-images-grid' },
                  optionalItems.map(function (optItem) {
                    return React.createElement('div', { key: optItem.id, className: 'optional-img-item' },
                      React.createElement('span', { className: 'optional-img-chip' }, 'Optional'),
                      React.createElement('button', {
                        type: 'button',
                        className: 'optional-img-remove',
                        onClick: function () { handleRemoveImageItem(optItem); },
                        title: 'Remove image'
                      }, React.createElement(TrashIcon, null)),
                      React.createElement('img', {
                        src: optItem.src,
                        alt: 'Optional product image',
                        onError: function (e) { if (optItem.rawPath) handleImgError(e, optItem.rawPath); }
                      })
                    );
                  }),
                  React.createElement('button', {
                    type: 'button',
                    className: 'add-more-images-tile',
                    onClick: function () { if (fileInputRef.current) fileInputRef.current.click(); }
                  },
                    React.createElement(UploadIcon, null),
                    'Add More Images'
                  )
                )
              )
            );
          })()
        ),

        React.createElement('div', { className: 'modal-grid' },
          React.createElement('div', { className: 'fl modal-full' },
            React.createElement('label', null,
              'Product Name *',
              React.createElement('span', { className: 'char-count' + (name.length >= NAME_LIMIT ? ' char-warn' : '') }, name.length + '/' + NAME_LIMIT)
            ),
            React.createElement('input', {
              type: 'text', placeholder: 'e.g. White Magnetic Board', value: name,
              maxLength: NAME_LIMIT,
              className: nameError ? 'input-error' : '',
              onChange: function (e) {
                var val = e.target.value;
                setName(val);
                setNameError(validateProductName(val));
              }
            }),
            nameError && React.createElement('div', {
              style: {
                color: '#f87171', fontSize: 12, marginTop: 6,
                fontFamily: "'Arial','Outfit',sans-serif",
              }
            }, nameError)
          ),
          React.createElement(VariantsEditor, {
            rows: variants,
            onAdd: addVariantRow,
            onRemove: removeVariantRow,
            onChange: updateVariantField,
            error: variantsError,
            expandedId: expandedVariantId,
            onToggleExpand: toggleExpandVariant,
            onViewVariants: function () { setShowVariantsModal(true); }
          }),
          React.createElement('div', { className: 'fl' },
            React.createElement('label', null, 'Category *'),
            React.createElement(CustomSelect, {
              value: categoryId,
              onChange: handleCategoryChange,
              options: [{ value: '', label: 'Select category' }].concat(
                categories.map(function (c) { return { value: c._id || c.id, label: c.name }; })
              )
            })
          ),
          React.createElement('div', { className: 'fl' },
            React.createElement('label', null, 'Subcategory *'),
            React.createElement(CustomSelect, {
              value: subcategoryId,
              disabled: !categoryId,
              onChange: setSubId,
              options: [{ value: '', label: categoryId ? 'Select subcategory' : 'Select category first' }].concat(
                subsToShow.map(function (s) { return { value: s._id || s.id, label: s.name }; })
              )
            })
          ),
          React.createElement('div', { className: 'fl modal-full' },
            React.createElement('label', null, 'Status'),
            React.createElement(CustomSelect, {
              value: productStatus,
              onChange: setProductStatus,
              options: [
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]
            })
          ),
          React.createElement('div', { className: 'fl modal-full' },
            React.createElement('label', null,
              'Description',
              React.createElement('span', { className: 'char-count' + (stripHtml(description).length >= DESC_LIMIT ? ' char-warn' : '') }, stripHtml(description).length + '/' + DESC_LIMIT)
            ),
            React.createElement(ReactQuill, {
              theme: 'snow',
              value: description,
              onChange: function (val) {
                setDescription(val);
              },
              modules: quillModules,
              placeholder: 'Product description...'
            })
          )
        ),

        React.createElement('div', { className: 'fa' },
          React.createElement('button', { className: 'btn btn-g', type: 'button', onClick: closeModal }, 'Cancel'),
          React.createElement('button', {
            className: 'btn btn-p', type: 'button',
            disabled: submitLoading || !!variantsError || !!nameError || !!imageError, style: { opacity: (submitLoading || variantsError || nameError || imageError) ? 0.7 : 1 },
            onClick: handleSubmit
          }, submitLoading ? 'Saving...' : (editId ? 'Update Product' : 'Add Product'))
        )
      )
    ),

    // ── Delete Confirm Modal ──────────────────────────────────────
    pendingDelete && React.createElement(DeleteConfirmModal, {
      itemName: pendingDelete.name || pendingDelete.title,
      message: 'Are you sure you want to delete this product?',
      onConfirm: confirmDelete,
      onCancel: cancelDelete
    }),

    // ── Delete Success Modal ──────────────────────────────────────
    deleteSuccess && React.createElement(DeleteSuccessModal, {
      isOpen: true,
      message: 'Deleted Successfully',
      subtitle: 'The product has been deleted successfully.',
      onClose: handleDeleteSuccessClose
    }),

    msg.text && !showModal && React.createElement('div', { className: 'mbx ' + (msg.type === 'e' ? 'mbx-e' : 'mbx-s') }, msg.text),

    React.createElement('div', { className: 'page-header-row' },
      React.createElement('div', { className: 'page-header-main' },
        React.createElement('div', { className: 'page-header-title-row' },
          React.createElement('div', { className: 'page-header-icon' },
            React.createElement('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
              React.createElement('path', { d: 'M4 4h16v16H4z' }),
              React.createElement('path', { d: 'M4 8h16M8 4v16' })
            )
          ),
          React.createElement('div', { className: 'ptitle' }, 'Products')
        ),
        React.createElement('div', { className: 'header-subtitle' }, 'View all products and inventory')
      ),
      canCreateProduct && React.createElement('button', { className: 'add-btn', type: 'button', onClick: function () { resetForm(); setShowModal(true); } },
        React.createElement('svg', { viewBox: '0 0 24 24', style: { width: 15, height: 15, fill: '#fff' } },
          React.createElement('path', { d: 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z' })
        ), ' Add Product'
      )
    ),

    React.createElement(StatCards, { cards: productStatCards }),

    React.createElement('div', { className: 'wid' },
      React.createElement('div', { className: 'wh' },
        React.createElement('span', { className: 'wt' },
          (statusFilter === 'all' && !search && !fromDate && !toDate)
            ? ('All Products (' + totalProductsCount + ')')
            : (statusFilter === 'active'
              ? ('Active Products (' + totalFiltered + ' of ' + totalProductsCount + ')')
              : (statusFilter === 'inactive'
                ? ('Inactive Products (' + totalFiltered + ' of ' + totalProductsCount + ')')
                : (statusFilter === 'out_of_stock'
                  ? ('Out of Stock Products (' + totalFiltered + ' of ' + totalProductsCount + ')')
                  : ('Products (' + totalFiltered + ' of ' + totalProductsCount + ')')
                )
              )
            )
        )
      ),

      React.createElement('div', { className: 'category-controls product-controls' },
        React.createElement('div', { className: 'category-search-field' },
          React.createElement('span', { className: 'category-search-icon', 'aria-hidden': 'true' }, '\u2315'),
          React.createElement('input', {
            type: 'text',
            value: search,
            placeholder: 'Search products by name, category...',
            'aria-label': 'Search products',
            onChange: function (e) { setSearch(e.target.value); setCurrentPage(1); }
          })
        ),
        React.createElement('div', { className: 'category-date-item' },
          React.createElement('span', { className: 'category-date-label' }, 'From'),
          React.createElement('input', {
            type: 'date',
            value: fromDate,
            'aria-label': 'Filter products from date',
            onChange: function (e) { setFromDate(e.target.value); setCurrentPage(1); }
          })
        ),
        React.createElement('div', { className: 'category-date-item' },
          React.createElement('span', { className: 'category-date-label' }, 'To'),
          React.createElement('input', {
            type: 'date',
            value: toDate,
            min: fromDate || undefined,
            'aria-label': 'Filter products to date',
            onChange: function (e) { setToDate(e.target.value); setCurrentPage(1); }
          })
        ),
        (search || fromDate || toDate || statusFilter !== 'all') && React.createElement('button', {
          type: 'button',
          className: 'category-clear-btn',
          onClick: function () { setSearch(''); setFromDate(''); setToDate(''); setStatusFilter('all'); setCurrentPage(1); }
        }, 'Clear')
      ),

      loading
        ? React.createElement('div', { className: 'loading-spin' })
        : React.createElement(React.Fragment, null,
          React.createElement('div', { style: { overflowX: 'auto' } },
            React.createElement('table', { className: 'tbl' },
              React.createElement('thead', null,
                React.createElement('tr', null,
                  React.createElement('th', null, 'S.No'),
                  React.createElement('th', null, 'Image'),
                  React.createElement('th', null, 'Name'),
                  React.createElement('th', null, 'Price'),
                  React.createElement('th', null, 'Stock'),
                  React.createElement('th', null, 'Category'),
                  React.createElement('th', null, 'Subcategory'),
                  React.createElement('th', null, 'Status'),
                  React.createElement('th', { className: 'actions-th' }, 'Actions')
                )
              ),
              React.createElement('tbody', null,
                pageItems.length === 0
                  ? React.createElement('tr', null,
                    React.createElement('td', { colSpan: 9, style: { textAlign: 'center', color: '#94a3b8', padding: 32 } }, 'No products found.')
                  )
                  : pageItems.map(function (p, idx) {
                    var catName = resolveName(p.category, categories) || resolveName(p.categoryId, categories);
                    var subName = resolveName(p.subcategory, subcategories) || resolveName(p.subcategoryId, subcategories);
                    var stock = stockDisplay(p);
                    var pid = p._id || p.id || String(idx);
                    return React.createElement('tr', { key: pid || idx },
                      React.createElement('td', { style: { color: '#ffffff', fontSize: 12 } }, (currentPage - 1) * ITEMS_PER_PAGE + idx + 1),
                      React.createElement('td', null,
                        React.createElement('div', { style: thumbStyle },
                          React.createElement(ProductImage, { product: p, alt: p.name })
                        )
                      ),
                      React.createElement('td', { className: 'prod-name' }, p.name),
                      React.createElement('td', { style: { color: '#34d399', fontWeight: 700 } },
                        (function () {
                          var list = p.variants;
                          if (!Array.isArray(list) || !list.length) return '\u20B9' + (p.price !== undefined ? p.price : '\u2014');
                          var min = Math.min.apply(null, list.map(function (v) { return Number(v.price) || 0; }));
                          var max = Math.max.apply(null, list.map(function (v) { return Number(v.price) || 0; }));
                          return min === max ? ('\u20B9' + min) : ('From \u20B9' + min);
                        })()
                      ),
                      React.createElement('td', { className: stock.cls }, stock.label),
                      React.createElement('td', { className: 'prod-cat' }, catName),
                      React.createElement('td', { className: 'prod-subcat' }, subName),
                      React.createElement('td', null,
                        React.createElement('button', {
                          type: 'button',
                          title: 'Click to toggle status',
                          disabled: togglingId === pid || !canEditProduct,
                          onClick: function (e) { e.stopPropagation(); handleToggleStatus(pid, p.status); },
                          className: 'bdg ' + ((p.status || '').toLowerCase() === 'inactive' ? 'bdg-b' : 'bdg-g'),
                          style: { cursor: togglingId === pid ? 'wait' : (canEditProduct ? 'pointer' : 'not-allowed'), border: 'none', opacity: (togglingId === pid || !canEditProduct) ? 0.6 : 1 }
                        }, togglingId === pid ? '...' : (p.status || 'active'))
                      ),
                      React.createElement('td', { className: 'actions-cell' },
                        React.createElement('div', { className: 'row-actions' },
                          React.createElement('button', {
                            className: 'row-action-btn row-action-view',
                            title: 'View',
                            'aria-label': 'View',
                            type: 'button',
                            onClick: function (e) { e.stopPropagation(); handleInlineView(pid); }
                          }, React.createElement(EyeIcon, null)),
                          canEditProduct && React.createElement('button', {
                            className: 'row-action-btn row-action-edit',
                            title: 'Edit',
                            'aria-label': 'Edit',
                            type: 'button',
                            onClick: function (e) { e.stopPropagation(); handleInlineEdit(pid); }
                          }, React.createElement(PencilIcon, null)),
                          canDeleteProduct && React.createElement('button', {
                            className: 'row-action-btn row-action-delete',
                            title: 'Delete',
                            'aria-label': 'Delete',
                            type: 'button',
                            onClick: function (e) { e.stopPropagation(); requestDelete(p); }
                          }, React.createElement(TrashIcon, null))
                        )
                      )
                    );
                  })
              )
            )
          ),
          React.createElement(Pagination, { currentPage: currentPage, totalPages: totalPages, onPageChange: setCurrentPage, totalItems: totalFiltered })
        )
    )
  );
};

export default Products;
